import mushroom from "@/pmcc-api";
import SharedAuthService from "@/service/SharedAuthService";
import DepartmentApi from "@/moduleApi/DepartmentApi";
import { getFoodImageUrl } from "./lunchFoodImage";
import {
  LUNCH_FOOD_STATUS,
  getCategoryCode,
  getCategoryLabel,
  setDefaultFoodImages
} from "./lunchFoodAssets";

function rootApiUrl() {
  return (window.MyConfig && window.MyConfig.hostApi) || mushroom._fnGetRootApiUrl();
}

function buildUrl(path, params = {}) {
  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value);
    }
  });
  const base = rootApiUrl().replace(/\/?$/, "/");
  const normalizedPath = String(path || "").replace(/^\//, "");
  const queryText = query.toString();
  return `${base}${normalizedPath}${queryText ? `?${queryText}` : ""}`;
}

async function request(path, options = {}) {
  const token = SharedAuthService.getLocalToken();
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json"
  };
  if (token) headers.Token = token;

  const method = options.method || "POST";
  const body = method === "GET" ? undefined : options.body || (options.params ? options.params : undefined);

  const response = await fetch(buildUrl(path, options.params), {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  });
  const payload = await response.json().catch(() => ({}));

  if (!response.ok || payload.code < 0) {
    const result = payload.result || payload;
    const message =
      result.message ||
      payload.message ||
      result.details ||
      payload.details ||
      result.error ||
      payload.error ||
      result.error_code ||
      payload.error_code ||
      "Không thể kết nối API cơm trưa.";
    const error = new Error(message);
    error.status = response.status;
    error.result = result;
    throw error;
  }

  return {
    result: payload.result !== undefined ? payload.result : payload,
    meta: payload.meta || {}
  };
}

function normalizeFood(food) {
  if (!food) return null;
  const category = getCategoryLabel(food.category);
  const imageId = food.image || food.Image || "";
  return {
    ...food,
    id: food.id || food.Id,
    category,
    categoryCode: getCategoryCode(food.category),
    name: food.name || food.Name || "",
    description: food.description || food.Description || "",
    price: Number(food.price || food.Price || 0),
    image: getFoodImageUrl(imageId, category),
    imageId,
    status: food.is_active === false ? LUNCH_FOOD_STATUS.INACTIVE : LUNCH_FOOD_STATUS.ACTIVE,
    isActive: food.is_active !== false
  };
}

function normalizeRegistration(registration) {
  if (!registration) return null;
  const items = (registration.items || []).map((item) => ({
    ...item,
    foodId: item.food_id || item.foodId,
    foodName: item.food_name || item.foodName || "",
    category: getCategoryLabel(item.category),
    price: Number(item.unit_price || item.price || 0),
    quantity: Number(item.quantity || 1),
    total: Number(item.amount || 0)
  }));
  return {
    ...registration,
    id: registration.id || registration.Id,
    employeeId: registration.employee_id || registration.employeeId,
    employeeNo: registration.employee_no || registration.employeeNo || "",
    employeeName: registration.employee_name || registration.employeeName || "",
    departmentId: registration.department_id || registration.departmentId || "",
    departmentName: registration.department_name || registration.departmentName || "",
    date: registration.date,
    items,
    note: registration.note || "",
    totalAmount: Number(registration.total_amount || registration.totalAmount || 0),
    status: registration.status || ""
  };
}

function normalizeBulkRegistrationResult(result) {
  return {
    ...(result || {}),
    registrations: ((result && result.registrations) || [])
      .map(normalizeRegistration)
      .filter(Boolean)
  };
}

const LunchApi = {
  async listFoods(params = {}) {
    const query = {
      search: params.search,
      category: params.category ? getCategoryCode(params.category) : "",
      is_active: params.isActive,
      limit: params.limit || 100000,
      offset: params.offset || 0
    };
    const response = await request("lunch/foods/list", { params: query });
    setDefaultFoodImages(response.meta.default_food_images || {});
    return {
      result: (response.result || []).map(normalizeFood).filter(Boolean),
      meta: response.meta
    };
  },

  async createFood(food) {
    const response = await request("lunch/foods/create", {
      body: {
        name: food.name,
        category: getCategoryCode(food.category),
        description: food.description,
        price: food.price,
        image: food.image,
        is_active: food.status !== LUNCH_FOOD_STATUS.INACTIVE
      }
    });
    return normalizeFood(response.result);
  },

  async updateFood(id, food) {
    const response = await request("lunch/foods/update", {
      body: {
        id,
        name: food.name,
        category: getCategoryCode(food.category),
        description: food.description,
        price: food.price,
        image: food.image,
        is_active: food.status !== LUNCH_FOOD_STATUS.INACTIVE
      }
    });
    return normalizeFood(response.result);
  },

  async deleteFood(id) {
    return request("lunch/foods/delete", { body: { id } });
  },

  async getMenuCalendar(month) {
    const response = await request("lunch/menus/calendar", { params: { month } });
    return response.result || {};
  },

  async getMenuDetail(date) {
    const response = await request("lunch/menus/detail", { params: { date } });
    return {
      ...response.result,
      foods: (response.result.foods || []).map(normalizeFood).filter(Boolean)
    };
  },

  async saveMenusBulk(dates, foodIds, overwrite = false) {
    return request("lunch/menus/bulk", {
      body: { dates, food_ids: foodIds, overwrite }
    });
  },

  async updateMenu(date, foodIds) {
    return request("lunch/menus/update", {
      body: { date, food_ids: foodIds }
    });
  },

  async deleteMenu(date) {
    return request("lunch/menus/delete", {
      body: { date }
    });
  },

  async getValidDates(month) {
    const response = await request("lunch/calendar/valid-dates", { params: { month } });
    return response.result || {};
  },

  async getSettings() {
    const response = await request("lunch/settings/detail", { method: "GET" });
    const settings = response.result || {};
    setDefaultFoodImages(settings.default_food_images || {});
    return settings;
  },

  async updateSettings(registrationDeadline, autoRegisterDefault) {
    const payload = { registration_deadline: registrationDeadline };
    if (typeof autoRegisterDefault === "boolean") {
      payload.auto_register_default = autoRegisterDefault;
    }
    const response = await request("lunch/settings/update", {
      body: payload
    });
    return response.result || {};
  },

  async listMyRegistrations(month) {
    const response = await request("lunch/registrations/me", { params: { month } });
    return response.result || { registrations: [] };
  },

  async getMyRegistration(date) {
    const response = await request("lunch/registrations/me/detail", { params: { date } });
    return normalizeRegistration(response.result);
  },

  async createRegistration(date, items, note) {
    const response = await request("lunch/registrations/create", {
      body: { date, items: items.map((item) => ({ food_id: item.foodId, quantity: item.quantity })), note }
    });
    return normalizeRegistration(response.result);
  },

  async createMyRegistrationsByWeek(weekStart) {
    const response = await request("lunch/registrations/create-week", {
      body: { week_start: weekStart }
    });
    return normalizeBulkRegistrationResult(response.result);
  },

  async createMyRegistrationsByMonth(month) {
    const response = await request("lunch/registrations/create-month", {
      body: { month }
    });
    return normalizeBulkRegistrationResult(response.result);
  },

  async updateMyRegistration(date, items, note) {
    const response = await request("lunch/registrations/me/update", {
      body: { date, items: items.map((item) => ({ food_id: item.foodId, quantity: item.quantity })), note }
    });
    return normalizeRegistration(response.result);
  },

  async cancelMyRegistration(date) {
    return request("lunch/registrations/me/cancel", { body: { date } });
  },

  async cancelMyRegistrationsByWeek(weekStart) {
    const response = await request("lunch/registrations/me/cancel-week", {
      body: { week_start: weekStart }
    });
    return response.result || {};
  },

  async cancelMyRegistrationsByMonth(month) {
    const response = await request("lunch/registrations/me/cancel-month", {
      body: { month }
    });
    return response.result || {};
  },

  async listRegistrations(params = {}) {
    const requestedLimit = Math.max(Number(params.limit) || 20, 1);
    const initialOffset = Math.max(Number(params.offset) || 0, 0);
    const pageSize = Math.min(requestedLimit, 200);
    const firstResponse = await request("lunch/registrations/list", {
      params: { ...params, limit: pageSize, offset: initialOffset }
    });
    const registrations = (firstResponse.result || []).map(normalizeRegistration).filter(Boolean);
    const total = Number(firstResponse.meta.total) || registrations.length;
    const targetCount = Math.min(requestedLimit, Math.max(total - initialOffset, 0));

    let offset = initialOffset + registrations.length;
    while (registrations.length < targetCount) {
      const response = await request("lunch/registrations/list", {
        params: {
          ...params,
          limit: Math.min(200, targetCount - registrations.length),
          offset
        }
      });
      const page = (response.result || []).map(normalizeRegistration).filter(Boolean);
      registrations.push(...page);
      offset += page.length;
      if (!page.length) break;
    }

    return {
      result: registrations,
      meta: { ...firstResponse.meta, limit: requestedLimit, offset: initialOffset }
    };
  },

  async listRegistrationHistory(params = {}) {
    const response = await request("lunch/registrations/history", {
      params: {
        scope: params.scope || "me",
        from_date: params.fromDate,
        to_date: params.toDate,
        status: params.status,
        department_id: params.departmentId,
        employee_id: params.employeeId,
        search: params.search,
        limit: params.limit || 20,
        offset: params.offset || 0
      }
    });
    return {
      result: (response.result || []).map(normalizeRegistration).filter(Boolean),
      meta: response.meta
    };
  },

  async getMonthlyReport(month, groupBy) {
    const response = await request("lunch/reports/monthly", {
      params: { month, group_by: groupBy }
    });
    return response.result || {};
  },

  async listDepartments() {
    const response = await DepartmentApi.list({ filters: "disabled!=true", limit: 1000000 }, true);
    return response.result || [];
  }
};

export default LunchApi;
